import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../services/supabaseClient';
import ManualBookingForm from './ManualBookingForm';
import BookingDetails from './BookingDetails';

const NAMES = {
  standard: 'Standardstädning', deep: 'Djupstädning', moving: 'Flyttstädning', office: 'Kontorsstädning',
  help: 'Flytthjälp', window: 'Fönsterputs', package: 'Månadspaket', minipaket: 'Minipaket'
};
const FIELDS = [['name', 'Namn'], ['phone', 'Telefon'], ['address', 'Adress'], ['city', 'Stad'], ['postal_code', 'Postnummer']];
const COLS = 'id, name, email, phone, address, city, postal_code, role, created_at, created_by_admin';
const card = { backgroundColor: '#fff', border: '1px solid #e8e4d9', borderRadius: '16px', padding: '20px' };
const inp = { width: '100%', padding: '10px 12px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', color: '#1a1a2e', outline: 'none', fontSize: '14px' };
const likeSafe = (s) => (s || '').replace(/[\\%_]/g, m => '\\' + m);
const randomPassword = () => {
  const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const arr = new Uint32Array(10);
  crypto.getRandomValues(arr);
  return Array.from(arr, (n) => chars[n % chars.length]).join('');
};

const AdminCustomers = ({ openEmail }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [editing, setEditing] = useState(false);
  const [profile, setProfile] = useState({});
  const [reload, setReload] = useState(0);
  const [reloadCust, setReloadCust] = useState(0);
  const [notice, setNotice] = useState(null);
  const opened = useRef(false);

  useEffect(() => {
    supabase.from('users').select(COLS).eq('role', 'customer').order('created_at', { ascending: false })
      .then(({ data }) => {
        const list = data || [];
        setCustomers(list);
        setLoading(false);
        if (openEmail && !opened.current) {
          opened.current = true;
          const found = list.find(c => (c.email || '').toLowerCase() === openEmail.toLowerCase());
          if (found) setSelected(found);
        }
      });
  }, [reloadCust]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) { setBookings([]); return; }
    supabase.from('bookings').select('*').ilike('email', likeSafe(selected.email))
      .order('date', { ascending: false })
      .then(({ data }) => setBookings(data || []));
  }, [selected, reload]);

  const handleSaved = (info) => {
    const lines = [];
    if (info && info.mailMsg) lines.push(info.mailMsg);
    if (info && info.accountError) lines.push(info.accountError);
    setNotice({
      title: info && info.newAccount ? '✅ Bokningen sparad och ett nytt kundkonto skapades' : '✅ Bokningen sparad',
      lines,
      account: info && info.newAccount ? info.newAccount : null
    });
    setShowForm(false);
    setEditBooking(null);
    setReload(r => r + 1);
    setReloadCust(r => r + 1);
  };

  const startEdit = () => {
    setProfile({
      name: selected.name || '', phone: selected.phone || '', address: selected.address || '',
      city: selected.city || '', postal_code: selected.postal_code || '', newPassword: ''
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    if (!profile.name.trim()) { alert('Namn kan inte vara tomt'); return; }
    const { newPassword, ...fields } = profile;
    const update = { ...fields };
    const pw = (newPassword || '').trim();
    if (pw) {
      if (pw.length < 6) { alert('Lösenordet måste vara minst 6 tecken'); return; }
      update.password = pw;
    }
    const { error } = await supabase.from('users').update(update).eq('id', selected.id);
    if (error) { alert('Kunde inte spara: ' + error.message); return; }
    const updated = { ...selected, ...fields };
    setSelected(updated);
    setCustomers(cs => cs.map(c => (c.id === updated.id ? updated : c)));
    setEditing(false);
    if (pw) setNotice({ title: '🔑 Nytt lösenord sparat', lines: [], account: { email: selected.email, password: pw } });
  };

  const renderNotice = () => {
    if (!notice) return null;
    return (
      <div className="mb-4 p-4 rounded-2xl" style={{ backgroundColor: '#dcfce7', border: '1px solid #86efac' }}>
        <div className="flex justify-between items-start gap-3">
          <p className="font-bold text-sm" style={{ color: '#166534' }}>{notice.title}</p>
          <button onClick={() => setNotice(null)} className="text-sm font-bold" style={{ color: '#166534' }}>✕</button>
        </div>
        {notice.lines.map((l, i) => <p key={i} className="text-sm mt-1" style={{ color: '#166534' }}>{l}</p>)}
        {notice.account && (
          <div className="mt-3 p-3 rounded-xl text-sm" style={{ backgroundColor: '#fff' }}>
            <p><strong>Email:</strong> {notice.account.email}</p>
            <p><strong>Lösenord:</strong> {notice.account.password}</p>
            <p className="text-xs mt-1" style={{ color: '#888' }}>Spara lösenordet nu, det visas bara här. Ge det till kunden om hen vill logga in.</p>
            <button onClick={() => navigator.clipboard.writeText(`Email: ${notice.account.email}\nLösenord: ${notice.account.password}`)}
              className="mt-2 px-4 py-2 rounded-full font-bold text-xs" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
              Kopiera
            </button>
          </div>
        )}
      </div>
    );
  };

  if (showForm) {
    return (
      <ManualBookingForm
        key={editBooking ? editBooking.id : 'new'}
        customer={selected}
        lastBooking={bookings[0]}
        editBooking={editBooking}
        onSaved={handleSaved}
        onCancel={() => { setShowForm(false); setEditBooking(null); }}
      />
    );
  }

  if (selected) {
    return (
      <div className="max-w-3xl" style={{ color: '#1a1a2e' }}>
        <button onClick={() => { setSelected(null); setEditing(false); }} className="text-sm font-semibold mb-4" style={{ color: '#C9A84C' }}>← Alla kunder</button>
        {renderNotice()}
        <div style={card} className="mb-6">
          <div className="flex justify-between items-start flex-wrap gap-3">
            <div>
              <h2 className="text-2xl font-bold">{selected.name}</h2>
              <p className="text-sm" style={{ color: '#888' }}>{selected.email}</p>
              {selected.created_by_admin && <p className="text-xs mt-1" style={{ color: '#92400e' }}>Konto skapat av admin</p>}
            </div>
            {!editing && (
              <button onClick={startEdit} className="px-4 py-2 rounded-full font-bold text-sm" style={{ backgroundColor: '#f8f7f4', border: '2px solid #e8e4d9' }}>
                ✏️ Redigera uppgifter
              </button>
            )}
          </div>

          {editing ? (
            <div className="mt-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>{label}</label>
                    <input style={inp} value={profile[k] || ''} onChange={e => setProfile(p => ({ ...p, [k]: e.target.value }))} />
                  </div>
                ))}
                <div className="sm:col-span-2">
                  <label className="block text-xs font-bold mb-1" style={{ color: '#888' }}>Nytt lösenord (lämna tomt för att behålla nuvarande)</label>
                  <div className="flex gap-2">
                    <input style={inp} value={profile.newPassword || ''} onChange={e => setProfile(p => ({ ...p, newPassword: e.target.value }))} />
                    <button type="button" onClick={() => setProfile(p => ({ ...p, newPassword: randomPassword() }))}
                      className="px-4 rounded-xl font-bold text-sm" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>Slumpa</button>
                  </div>
                </div>
              </div>
              <p className="text-xs mt-2" style={{ color: '#888' }}>Emailen kan inte ändras här eftersom bokningarna kopplas till den.</p>
              <div className="flex gap-3 mt-4">
                <button onClick={() => setEditing(false)} className="flex-1 py-3 rounded-full font-bold" style={{ backgroundColor: '#f8f7f4', color: '#555', border: '2px solid #e8e4d9' }}>Avbryt</button>
                <button onClick={saveProfile} className="flex-1 py-3 rounded-full font-bold" style={{ backgroundColor: '#16a34a', color: '#fff' }}>Spara</button>
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm space-y-1" style={{ color: '#555' }}>
              <p>📞 {selected.phone || '—'}</p>
              <p>📍 {selected.address || '—'}{selected.postal_code || selected.city ? `, ${selected.postal_code || ''} ${selected.city || ''}` : ''}</p>
            </div>
          )}

          <button onClick={() => { setEditBooking(null); setShowForm(true); }} className="mt-4 px-5 py-3 rounded-full font-bold text-sm" style={{ backgroundColor: '#1a1a2e', color: '#C9A84C' }}>
            + Lägg till manuell bokning
          </button>
        </div>

        <h3 className="font-bold mb-3">Bokningar ({bookings.length})</h3>
        {bookings.length === 0 && <p style={{ color: '#888' }}>Inga bokningar ännu.</p>}
        <div className="space-y-4">
          {bookings.map(b => (
            <div key={b.id} style={card}>
              <div className="flex justify-between flex-wrap gap-2 mb-3">
                <div>
                  <p className="font-bold">
                    {NAMES[b.service] || b.service} — {b.date} kl {b.time}
                    {b.details?.dates?.length > 1 ? ` (${b.details.dates.length} städningar)` : ''}
                  </p>
                  <p className="text-xs" style={{ color: '#888' }}>
                    {b.status === 'completed' ? 'SLUTFÖRD' : b.status === 'cancelled' ? 'AVBOKAD' : 'KOMMANDE'}{b.details?.manual ? ' • MANUELL' : ''}
                  </p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-lg" style={{ color: '#C9A84C' }}>{Math.round(b.price || 0).toLocaleString()} kr</p>
                  {b.rut_discount > 0 && (
                    <p className="text-xs" style={{ color: '#888' }}>Kunden betalar {Math.round(b.final_price || 0).toLocaleString()} kr</p>
                  )}
                </div>
              </div>
              <BookingDetails details={b.details} />
              {b.details?.manual && (
                <button onClick={() => { setEditBooking(b); setShowForm(true); }}
                  className="mt-1 px-4 py-2 rounded-full font-bold text-sm" style={{ backgroundColor: '#f8f7f4', border: '2px solid #e8e4d9' }}>
                  ✏️ Redigera bokning
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  const shown = customers.filter(c => `${c.name} ${c.email}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="max-w-3xl" style={{ color: '#1a1a2e' }}>
      {renderNotice()}
      <div className="flex justify-between items-center flex-wrap gap-3 mb-4">
        <h2 className="text-2xl font-bold">Kunder ({customers.length})</h2>
        <button onClick={() => { setSelected(null); setEditBooking(null); setShowForm(true); }} className="px-5 py-3 rounded-full font-bold text-sm" style={{ backgroundColor: '#C9A84C', color: '#1a1a2e' }}>
          + Ny manuell bokning
        </button>
      </div>
      <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Sök namn eller email..."
        style={{ width: '100%', padding: '10px 14px', borderRadius: '10px', border: '2px solid #e8e4d9', backgroundColor: '#f8f7f4', marginBottom: '16px', outline: 'none' }} />
      {loading && <p style={{ color: '#888' }}>Laddar...</p>}
      <div className="space-y-3">
        {shown.map(c => (
          <button key={c.id} onClick={() => setSelected(c)} className="w-full text-left" style={card}>
            <p className="font-bold">{c.name}</p>
            <p className="text-sm" style={{ color: '#888' }}>{c.email}{c.phone ? ` • ${c.phone}` : ''}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

export default AdminCustomers;
